import { parseISO, format, differenceInDays } from 'date-fns';

/**
 * Parse a date string from the API (YYYY-MM-DD) as a LOCAL date.
 * 
 * CRITICAL: new Date("2026-02-24") parses as UTC midnight, which shifts
 * to the previous day in US timezones. This function prevents that.
 */
export function parseLocalDate(dateStr: string | Date | null | undefined): Date | null {
  if (!dateStr) return null;
  
  // Date-only string (YYYY-MM-DD) — parse as local midnight
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  }
  
  // ISO datetime string — parse normally
  if (typeof dateStr === 'string' && dateStr.includes('T')) {
    return parseISO(dateStr);
  }
  
  // Already a Date object
  if (dateStr instanceof Date) return dateStr;
  
  // Fallback with warning
  console.warn(`dateUtils: unexpected date format: ${dateStr}`);
  return new Date(dateStr);
}

/**
 * Format a date string from the API for display.
 * Safe wrapper that handles null/undefined and timezone issues.
 */
export function formatDate(dateStr: string | Date | null | undefined, formatStr = 'MMM d, yyyy'): string {
  const date = parseLocalDate(dateStr);
  if (!date || isNaN(date.getTime())) return '—';
  return format(date, formatStr);
}

/**
 * Calculate business days between two date strings (inclusive).
 */
export function countBusinessDays(startStr: string | Date | null | undefined, endStr: string | Date | null | undefined): number {
  const start = parseLocalDate(startStr);
  const end = parseLocalDate(endStr);
  if (!start || !end) return 0;
  
  let count = 0;
  let current = new Date(start);
  while (current <= end) {
    const day = current.getDay();
    if (day !== 0 && day !== 6) count++;
    current.setDate(current.getDate() + 1);
  }
  return Math.max(1, count);
}

/**
 * Check if a date string falls on a business day.
 */
export function isBusinessDay(dateStr: string | Date | null | undefined): boolean {
  const date = parseLocalDate(dateStr);
  if (!date) return false;
  const day = date.getDay();
  return day !== 0 && day !== 6;
}

/**
 * Calculate days remaining from today to a target date string.
 * Returns negative for overdue dates.
 */
export function daysUntil(dateStr: string | Date | null | undefined): number | null {
  const target = parseLocalDate(dateStr);
  if (!target) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return differenceInDays(target, today);
}

/**
 * Check if a date string is in the past (overdue).
 */
export function isOverdue(dateStr: string | Date | null | undefined): boolean {
  const remaining = daysUntil(dateStr);
  return remaining !== null && remaining < 0;
}

/**
 * Parse a date string safely, returning null instead of epoch for invalid/null input.
 */
export function safeParseDateOrNull(dateStr: string | Date | null | undefined): Date | null {
  if (!dateStr) return null;
  const parsed = parseLocalDate(dateStr);
  if (parsed) return parsed;
  const fallback = new Date(dateStr as string);
  return isNaN(fallback.getTime()) ? null : fallback;
}

/**
 * Format a date for API submission (YYYY-MM-DD).
 */
export function toAPIDate(date: string | Date | null | undefined): string | null {
  if (!date) return null;
  if (typeof date === 'string') return date;
  return format(date, 'yyyy-MM-dd');
}
